import * as React from 'react'
import { connect } from 'react-redux'
import { handleOpeningHours } from '../../../utils/openingHours'
import type { RootState, Dispatch } from '../../../app/store'
import type { DeliveryType } from '../../../utils/deliveryHelper'
import type { MenuT, MealT, AddOnT } from '../../../models/menus'
import { getPrice, getFormattedPrice } from '../../../utils/formattedPrice'
import Page from '../../layout/Page'
import AddOns from '../../menus/AddOns'
import ModalAddOns from '../../menus/ModalAddOns'
import ClosedBanner from '../../ClosedBanner'
import Customizations from '../../Customizations'
import Modal from '../../Modal'
import Angle from '../../../icons/Angle'
import styles from './Product.module.css'
import { useNavigate, useParams } from 'react-router-dom'

interface ProductT {
  menus: Array<MenuT>;
  product?: MealT | null;
  addOns?: Array<AddOnT>;
  deliveryType: DeliveryType;
  fetchProduct: (payload: { menuId: string; productId: string }) => void;
  addItemToCart: (payload: {
    storeId: string;
    productId: string;
    itemType: number;
    customizationItems: Array<string>;
  }) => void;
  updateProduct: (payload: MealT | null) => void;
}

interface CustomizationMenuItem {
  id: string;
  customizationType: number;
  name: string;
  active: boolean;
  price: number;
  takeAwayPrice?: number;
  preSelected: boolean;
}
interface SelectedItem extends CustomizationMenuItem {
  selected: boolean;
}

interface Customization {
  id: string;
  name: string;
  active: boolean;
  customizationType: number;
  customizationMenuItems: CustomizationMenuItem[];
  items: SelectedItem[];
}

function Product ({
  menus,
  product,
  addOns,
  deliveryType,
  updateProduct,
  fetchProduct,
  addItemToCart,
}: ProductT) {
  const [addonModalIsActive, setAddonModalIsActive] = React.useState(false)
  const [contentsIsOpen, setContentsIsOpen] = React.useState(false)
  const [currentMenu, setCurrentMenu] = React.useState<MenuT | null>(null)
  const [customizationsToChange, setCustomizationsToChange] = React.useState<
    Array<Customization>
  >([])
  const [customizationsToAdd, setCustomizationsToAdd] = React.useState<
    Array<{
      id: string;
      name: string;
      price: number;
    }>
  >([])
  const [customizationsToRemove, setCustomizationsToRemove] = React.useState<
    Array<{
      id: string;
      name: string;
    }>
  >([])
  const [customizationsPrice, setCustomizationsPrice] = React.useState(0)
  const { name, description, nutrientFacts, image, customizations } =
    product || {}
  const { storeId, restaurantId, menuId, productId } = useParams()
  const navigate = useNavigate()

  React.useEffect(() => {
    if (menuId && productId) {
      fetchProduct({
        menuId,
        productId,
      })
    }
  }, [fetchProduct, menuId, productId])

  React.useEffect(() => {
    const menu = menus.find((item: MenuT) => item.id === restaurantId)
    if (menu) {
      setCurrentMenu(menu)
    }
  }, [menus, restaurantId])

  React.useEffect(() => {
    if (customizations?.customizable?.length > 0) {
      setCustomizationsToChange(customizations.customizable.map((group: Customization) => {
        return {
          ...group,
          items: group.customizationMenuItems.map((item) => {
            return {
              ...item,
              selected: item.preSelected || false
            }
          }
          )
        }
      }))

    }
  }, [customizations?.customizable])

  React.useEffect(() => {
    if (customizationsToChange.length > 0 || customizationsToAdd.length > 0) {
      const selectionsPrice = customizationsToChange.reduce((prev, current) => {
        const selectedItem = current.items?.find(
          (item) => item.selected === true
        )
        if (selectedItem) {
          return prev + getPrice(selectedItem, deliveryType)
        } else {
          return prev
        }
      }, 0)
      const addedPrice = customizationsToAdd.reduce((prev, current) => {
        const currentPrice = getPrice(current, deliveryType)
        return prev + currentPrice
      }, 0)
      setCustomizationsPrice(selectionsPrice + addedPrice)
    } else {
      setCustomizationsPrice(0)
    }
  }, [customizationsToChange, customizationsToAdd, deliveryType])

  function onAddProduct () {
    const selections = customizationsToChange.reduce<string[]>((prev, current) => {
      const selectedItem = current.items?.find(
        (item) => item.selected === true)
      if (selectedItem) {
        return [...prev, selectedItem.id]
      } else {
        return [...prev]
      }
    }, [])

    const customizationItems: Array<string> = [
      ...customizationsToAdd.map((ca) => ca.id),
      ...customizationsToRemove.map((cr) => cr.id),
      ...selections,
    ]
    if (storeId && productId) {
      addItemToCart({
        storeId,
        productId,
        itemType: 100,
        customizationItems,
      })
    }
    clearSelectedIngredients()
    if (addOns && addOns.length > 0) {
      setAddonModalIsActive(true)
    } else {
      onContinue()
    }
  }

  function clearSelectedIngredients () {
    if (product) {
      const updatedProduct = { ...product }
      updateProduct(updatedProduct)
    }
  }

  function onContinue () {
    navigate(`/butik/${storeId}/restaurant/${restaurantId}`)
  }

  let isOpen: boolean = true
  let openingDetails: { opening?: string; closes?: string } = {}

  if (currentMenu) {
    const openingHours = handleOpeningHours(currentMenu?.openingHours) || {}
    isOpen = openingHours.isOpen
    openingDetails = openingHours.openingDetails
  }

  function getTotalPrice () {
    const productPrice = product ? getPrice(product, deliveryType) : 0
    const customPrice = customizationsPrice || 0

    return productPrice + customPrice
  }

  return (
    <Page
      withTopNav={true}
      withBackButton={false}
      withCancelButton={true}
      withCart={true}
    >
      <div className={styles.product}>
        {currentMenu?.name && (
          <p className={styles.menuHeading}>{currentMenu?.name}</p>
        )}
        {currentMenu && !isOpen && (
          <ClosedBanner openingTime={openingDetails.opening || ''} />
        )}
        <div className={image ? styles.contentWithImage : styles.content}>
          <div className={styles.productContent}>
            <div className={styles.productDetails}>
              <>
                {image && (
                  <div className={styles.productImage}>
                    <img className={styles.image} src={image} alt={name} />
                  </div>
                )}
                <div className={styles.productInfo}>
                  <h1 className={styles.heading}>{name}</h1>
                  <p className={styles.description}>{description}</p>
                  {nutrientFacts && (
                    <button
                      onClick={() => setContentsIsOpen((prev) => !prev)}
                      type="button"
                      className={styles.toggleButton}
                    >
                      <span>Innehåll och näringsvärde</span>
                      <Angle
                        direction="down"
                        color="#cf2e05"
                        className={
                          contentsIsOpen
                            ? styles.activeToggleIcon
                            : styles.toggleIcon
                        }
                      />
                    </button>
                  )}
                  {contentsIsOpen && (
                    <p className={styles.nutrientFacts}>{nutrientFacts}</p>
                  )}
                </div>
              </>
            </div>
            {(customizations?.customizable?.length > 0 ||
              customizations?.addable?.length > 0 ||
              customizations?.removable?.length > 0) && (
              <div className={styles.customizations}>
                <Customizations
                  itemsToChange={customizationsToChange}
                  updateItemsToChange={setCustomizationsToChange}
                  customizations={customizations}
                  itemsToAdd={customizationsToAdd}
                  updateItemsToAdd={setCustomizationsToAdd}
                  itemsToRemove={customizationsToRemove}
                  updateItemsToRemove={setCustomizationsToRemove}
                />
              </div>
            )}
          </div>
          <div
            className={
              customizations?.customizable?.length > 0 ||
                customizations?.addable?.length > 0 ||
                customizations?.removable?.length > 0
                ? styles.summary
                : styles.summarySmall
            }
          >
            <div className={styles.actions}>
              <span className={styles.price}>
                {getFormattedPrice(getTotalPrice())}
              </span>
              <button
                disabled={currentMenu && isOpen === false ? true : false}
                className={styles.addButton}
                onClick={onAddProduct}
                type="button"
              >
                Lägg till
              </button>
            </div>
          </div>
        </div>
        <AddOns storeId={storeId} restaurantId={restaurantId} />
      </div>
      <Modal active={addonModalIsActive} maxWidth="1440px">
        <ModalAddOns
          onContinue={onContinue}
          storeId={storeId}
          restaurantId={restaurantId}
        />
      </Modal>
    </Page>
  )
}

function mapState (state: RootState) {
  return {
    product: state.menus.currentProduct,
    menus: state.menus.items,
    addOns: state.menus.addOns,
    deliveryType: state.cart.deliveryType,
  }
}
function mapDispatch (dispatch: Dispatch) {
  return {
    fetchProduct: dispatch.menus.fetchProduct,
    addItemToCart: dispatch.cart.addItemToCart,
    updateProduct: dispatch.menus.setProduct,
  }
}

export default connect(mapState, mapDispatch)(Product)
